// Budget alert row — horizontal bar with category icon, name, spent/limit, level color.
// Receives array of { category_id, name, icon, color, limit, spent, pct, level }.
// Backend sorts worst first (highest pct); only renders if any budget exists.
//
// Phase 48b refactor: use BudgetProgressBar + budgetTextColor / budgetBarColor
// from BudgetProgressBar.jsx so Dashboard and Budgets page read the same
// number the same way. Previously this component had its own inline
// `levelColor()` helper that duplicated the palette — easy to drift.
import React from 'react';
import { formatPercent } from '../lib/format.js';
import { useI18n } from '../i18n/index.jsx';
import { categoryLabel } from '../lib/categories.js';
import BudgetProgressBar, { budgetTextColor } from './BudgetProgressBar.jsx';
import { Money } from '../lib/privacy.jsx';

export default function BudgetAlerts({ alerts, currency = '$' }) {
  const { t } = useI18n();
  if (!alerts || alerts.length === 0) {
    return (
      <div className="text-center text-slate-400 dark:text-slate-500 py-4 text-sm">
        <p>{t('budgets.notSet')}</p>
        <p className="text-xs mt-1">{t('budgets.notSetHint')}</p>
      </div>
    );
  }
  const overCount = alerts.filter(a => a.level === 'over').length;
  const warnCount = alerts.filter(a => a.level === 'warn').length;
  const total = alerts.length;
  return (
    <div>
      {/* Summary header */}
      <div className="flex items-center gap-2 text-xs mb-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
          <span aria-hidden="true">📊</span>
          <span className="tabular-nums">{total}</span>
          <span>{t('budgets.budgetsCount')}</span>
        </span>
        {overCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
            <span className="tabular-nums">{overCount}</span>
            <span>{t('budgets.overCount')}</span>
          </span>
        )}
        {warnCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            <span className="tabular-nums">{warnCount}</span>
            <span>{t('budgets.warnCount')}</span>
          </span>
        )}
      </div>

      <ul className="space-y-3">
        {alerts.map(a => (
          <li key={a.category_id}>
            <div className="flex items-center justify-between text-sm mb-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span aria-hidden="true">{a.icon}</span>
                <span className="truncate font-medium text-slate-700 dark:text-slate-200">{categoryLabel({ name: a.name }, t)}</span>
                {a.level === 'over' && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">{t('budgets.over')}</span>
                )}
                {a.level === 'warn' && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">{t('budgets.warn')}</span>
                )}
              </div>
              <div className="flex items-center gap-2 tabular-nums shrink-0">
                <Money amount={a.spent} currency={currency} className={`font-medium ${a.level === 'over' ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-200'}`} />
                <span className="text-slate-400 dark:text-slate-500 text-xs">/ <Money amount={a.limit} currency={currency} /></span>
                <span className={budgetTextColor(a.level)}>{formatPercent(a.pct, 0)}</span>
              </div>
            </div>
            <BudgetProgressBar
              pct={a.pct}
              ariaLabel={t('budgets.progressAria', { name: categoryLabel({ name: a.name }, t), pct: formatPercent(a.pct, 0) })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
